/**
 * Blame on a phone (PhoneEditor board): there is no hover, so who wrote what is a bottom sheet. One row per block of
 * lines (who, person or AI agent with its model, when), the block of the tapped line first and marked; a row moves the
 * editor to its lines. The author's mark carries initials and a solid (person) or dashed (agent) edge: never a colour
 * alone.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { BottomSheet } from './BottomSheet';
import { ParticipantMark } from '../presence/Presence';
import { blameBlocks, blameHover } from '../../utils/blameModel';
import { initialsOfName } from '../../utils/liveModel';

const plain = (markdown) => String(markdown || '').replace(/\*\*|`/g, '').replace(/ {2}\n/g, ' · ');

export default function BlameSheet({ open, onClose, blame, focusLine = null, you = null, onGoToLine, onTurnOff }) {
  const blocks = blameBlocks(blame?.lines || []);
  const focused = blocks.find((b) => focusLine >= b.start && focusLine <= b.end) || null;
  const ordered = focused ? [focused, ...blocks.filter((b) => b !== focused)] : blocks;
  return (
    <BottomSheet
      footer={<button className="collab-btn collab-sheet-done" data-testid="blame-off" onClick={onTurnOff} type="button">Turn blame off</button>}
      onClose={onClose}
      open={open}
      testId="blame-sheet"
      title={focused ? `Line ${focusLine}` : 'Who wrote this file'}
    >
      {!blame && <div className="collab-muted">Loading who wrote each line…</div>}
      {blame && blocks.length === 0 && <div className="collab-muted">No history for this file yet.</div>}
      <div className="collab-sheet-list">
        {ordered.map((block) => {
          const { entry } = block;
          const agent = Boolean(entry.agent || entry.via);
          const slot = agent ? (entry.via === 'Flow' ? 3 : 4) : (entry.author?.slot ?? 0);
          return (
            <button
              aria-current={block === focused ? 'true' : undefined}
              className={`collab-sheet-row${block === focused ? ' is-focused' : ''}`}
              data-testid="blame-row"
              key={`${block.start}-${block.end}`}
              onClick={() => { onGoToLine(block.start); onClose(); }}
              type="button"
            >
              <ParticipantMark agent={agent} initials={entry.author?.initials || initialsOfName(entry.author?.name || entry.author?.email) || (agent ? 'AI' : '?')} size={28} slot={slot} />
              <span className="collab-sheet-row-main">
                <span>{block.label(you)}</span>
                <span className="collab-muted collab-small">{block.start === block.end ? `Line ${block.start}` : `Lines ${block.start}–${block.end}`} · {entry.uncommitted ? 'not saved yet' : plain(blameHover(entry, you))}</span>
              </span>
            </button>
          );
        })}
      </div>
    </BottomSheet>
  );
}

BlameSheet.propTypes = {
  blame: PropTypes.object,
  focusLine: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onGoToLine: PropTypes.func.isRequired,
  onTurnOff: PropTypes.func.isRequired,
  open: PropTypes.bool,
  you: PropTypes.object,
};
